<script setup lang="ts">
import ButtonClose from '@/assets/icons/Buttons/ButtonClose.vue';
import ButtonConfirm from '@/assets/icons/Buttons/ButtonConfirm.vue';
import ButtonMenu from '@/assets/icons/Buttons/ButtonMenu.vue';
import AddDishFromRecipesDialog from '@/components/Menus/AddDishFromRecipesDialog.vue';
import CategoryDialog from '@/components/Menus/MenuDetails/CategoryDialog.vue';
import DishDialog from '@/components/Menus/MenuDetails/DishDialog.vue';
import { useAccordionScroll } from '@/composables/useAccordionScroll';
import { useMenus } from '@/composables/useMenus';
import { usePermissions } from '@/composables/usePermissions';
import type { MessageSchema } from '@/i18n';
import { useDishesStore } from '@/stores/dishesStore';
import { useUserStore } from '@/stores/useUserStore';
import type { Dish } from '@/types/dish';
import type {
  CreateMenuCategoryRequest,
  CreateMenuDishRequest,
  MenuCategory,
  MenuDish,
  UpdateMenuCategoryRequest,
  UpdateMenuDishRequest,
} from '@/types/menu';
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import Button from 'primevue/button';
import Menu from 'primevue/menu';
import Textarea from 'primevue/textarea';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const { t, te } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });
const { can } = usePermissions();

const route = useRoute();
const router = useRouter();
const confirm = useConfirm();
const toast = useToast();

const {
  selectedMenu,
  menuCategories,
  isLoading,
  fetchMenu,
  fetchMenuCategories,
  updateMenu,
  deleteMenu,
  addCategoryToMenu,
  updateMenuCategory,
  removeMenuCategory,
  addDishToCategory,
  removeMenuDish,
  updateMenuDish,
  exportMenuToPdf,
} = useMenus();

const menuId = computed(() => parseInt(route.params.menuId as string));

// Accordion scroll-into-view logic
const { activeIndex: categoryActiveIndex, setHeaderRef: setCategoryHeaderRef } = useAccordionScroll<number[]>([]);

// Dialog states
const showCategoryDialog = ref(false);
const showDishDialog = ref(false);
const showAddFromRecipesDialog = ref(false);
const isEditingMenu = ref(false);
const userStore = useUserStore();
const dishesStore = useDishesStore();
const categoryDishMenus = ref<Record<number, { toggle?: (event: Event) => void } | null>>({});

// State to track dishes being generated
const generatingDishes = ref<Record<string, boolean>>({});

// Helper to check if a dish with a specific title in a category is being generated
const _isDishGenerating = (categoryId: number, dishName: string) => {
  const generationKey = `${categoryId}-${dishName}`;
  return generatingDishes.value[generationKey] || false;
};

// Form data
const editMenuName = ref('');
const selectedCategory = ref<MenuCategory | null>(null);
const selectedDish = ref<MenuDish | null>(null);

// State for managing which action to take
const dishCreationMode = ref<'write' | 'select'>('write');
const selectedCategoryForDish = ref<MenuCategory | null>(null);

// State for managing selected dish from recipes
const selectedDishFromRecipes = ref<Dish | null>(null);
const selectedDishTitleFromRecipes = ref('');

const menuOptions = ref();

const menuOptionsItems = computed(() => [
  {
    items: [
      {
        label: t('common.edit'),
        icon: 'pi pi-pencil',
        command: () => {
          editMenuName.value = selectedMenu.value?.name || '';
          isEditingMenu.value = true;
        },
      },
      {
        label: t('menu.export.label'),
        icon: 'pi pi-file-pdf',
        command: () => handleExportToPdf(),
      },
      {
        label: t('common.delete'),
        icon: 'pi pi-trash',
        command: () => confirmDeleteMenu(),
      },
      {
        label: t('menu.category.add.label'),
        icon: 'pi pi-plus',
        command: () => {
          selectedCategory.value = null;
          showCategoryDialog.value = true;
        },
      },
    ],
  },
]);

// Category-specific dish menu functions
const getCategoryDishMenuItems = (categoryId: number) => [
  {
    items: [
      {
        label: t('menu.dish.addFromRecipes'),
        icon: 'pi pi-book',
        command: () => handleSelectFromRecipesForCategory(categoryId),
      },
      {
        label: t('menu.dish.writeDish'),
        icon: 'pi pi-pencil',
        command: () => handleCreateDish(categoryId),
      },
    ],
  },
];

const toggleCategoryDishMenu = (event: MouseEvent, categoryId: number) => {
  if (!can('menus', 'update')) return;
  const menuRef = categoryDishMenus.value[categoryId];
  if (menuRef?.toggle) {
    menuRef.toggle(event);
  }
};

const handleSelectFromRecipesForCategory = (categoryId: number) => {
  if (!can('menus', 'update')) return;
  dishCreationMode.value = 'select';
  selectedCategoryForDish.value = menuCategories.value.find((cat) => cat.id === categoryId) || null;
  showAddFromRecipesDialog.value = true;
};

onMounted(async () => {
  if (menuId.value) {
    await fetchMenu(menuId.value);
    await fetchMenuCategories(menuId.value);
  }
});

watch(
  () => route.params.menuId,
  async (newMenuId) => {
    if (newMenuId) {
      await fetchMenu(parseInt(newMenuId as string));
      await fetchMenuCategories(parseInt(newMenuId as string));
    }
  },
);

const toggleMenuOptions = (event: Event) => {
  if (!can('menus', 'update') && !can('menus', 'delete')) return;
  menuOptions.value.toggle(event);
};

const handleUpdateMenu = async () => {
  if (!editMenuName.value.trim() || !selectedMenu.value || !can('menus', 'update')) return;

  const result = await updateMenu(selectedMenu.value.id, { name: editMenuName.value });
  if (result) {
    isEditingMenu.value = false;
  }
};

const confirmDeleteMenu = () => {
  if (!selectedMenu.value || !can('menus', 'delete')) return;

  confirm.require({
    message: t('menu.delete.confirm'),
    header: t('menu.delete.confirm'),
    icon: 'pi pi-exclamation-triangle',
    rejectClass: 'p-button-secondary p-button-outlined',
    rejectLabel: t('common.cancel'),
    acceptLabel: t('common.delete'),
    accept: async () => {
      if (selectedMenu.value) {
        const success = await deleteMenu(selectedMenu.value.id);
        if (success) {
          router.push('/my-recipes/my-menus');
        }
      }
    },
  });
};

const handleExportToPdf = async () => {
  if (!selectedMenu.value) return;
  await exportMenuToPdf(selectedMenu.value.id);
};

// Category handlers
const handleEditCategory = (category: MenuCategory) => {
  if (!can('menus', 'update')) return;
  selectedCategory.value = category;
  showCategoryDialog.value = true;
};

const handleCategorySave = async (data: CreateMenuCategoryRequest | UpdateMenuCategoryRequest) => {
  if (!can('menus', 'update')) return;

  let result;
  if (selectedCategory.value) {
    // Update existing category
    result = await updateMenuCategory(selectedCategory.value.id, data);
    // if (result) {
    //   toast.add({
    //     severity: 'success',
    //     summary: 'Success',
    //     detail: t('menu.category.update.success'),
    //     life: 3000,
    //   });
    // }
  } else {
    // Create new category
    result = await addCategoryToMenu(menuId.value, data);
  }

  if (result) {
    showCategoryDialog.value = false;
    selectedCategory.value = null;
    await fetchMenuCategories(menuId.value);
  }
};

const handleCategoryDelete = async (categoryId: number) => {
  if (!can('menus', 'delete')) return;

  const success = await removeMenuCategory(categoryId);
  if (success) {
    showCategoryDialog.value = false;
    selectedCategory.value = null;
    await fetchMenuCategories(menuId.value);
  }
};

// Dish handlers
const handleCreateDish = (categoryId?: number) => {
  if (!can('menus', 'update')) return;

  dishCreationMode.value = 'write';
  selectedDish.value = null;
  selectedCategoryForDish.value = categoryId ? menuCategories.value.find((cat) => cat.id === categoryId) || null : null;
  selectedDishFromRecipes.value = null;
  selectedDishTitleFromRecipes.value = '';
  showDishDialog.value = true;
};

const handleEditDish = (dish: MenuDish) => {
  if (!can('menus', 'update')) return;

  selectedDish.value = dish;
  selectedCategoryForDish.value = null;
  selectedDishFromRecipes.value = null;
  selectedDishTitleFromRecipes.value = '';
  showDishDialog.value = true;
};

const handleDishFromRecipesSelected = async (dish: Dish) => {
  if (!can('menus', 'update')) return;

  selectedDishFromRecipes.value = dish;
  selectedDishTitleFromRecipes.value = '';
  showDishDialog.value = true;
};

const handleDishTitleFromRecipesSelected = async (dishTitle: string) => {
  if (!can('menus', 'update')) return;

  selectedDishFromRecipes.value = null;
  selectedDishTitleFromRecipes.value = dishTitle;
  showDishDialog.value = true;
};

const handleDishSave = async (data: (CreateMenuDishRequest | UpdateMenuDishRequest) & { categoryId: number }) => {
  if (!can('menus', 'update')) return;

  let result;
  if (selectedDish.value) {
    // Update existing dish
    const { categoryId, ...updateData } = data;
    const updatePayload = {
      ...updateData,
      menu_category_id: categoryId,
    };
    result = await updateMenuDish(selectedDish.value.id, updatePayload);
    // if (result) {
    //   toast.add({
    //     severity: 'success',
    //     summary: 'Success',
    //     detail: t('menu.dish.update.success'),
    //     life: 3000,
    //   });
    // }
  } else {
    // Create new dish
    const { categoryId, ...createData } = data;
    result = await addDishToCategory(categoryId, createData);
  }

  if (result) {
    showDishDialog.value = false;
    selectedDish.value = null;
    selectedCategoryForDish.value = null;
    selectedDishFromRecipes.value = null;
    selectedDishTitleFromRecipes.value = '';
    await fetchMenuCategories(menuId.value);
  }
};

const handleDishDelete = async (dishId: number) => {
  if (!can('menus', 'delete')) return;

  const success = await removeMenuDish(dishId);
  if (success) {
    showDishDialog.value = false;
    selectedDish.value = null;
    await fetchMenuCategories(menuId.value);
  }
};

const handleGenerateFromTitle = async (data: { name: string; categoryId: number; dishId?: number }) => {
  if (!can('dishes', 'create') || !userStore.user?.restaurants?.[0]?.id) return;

  const { name, categoryId, dishId } = data;
  const restaurantId = userStore.user.restaurants[0].id;

  // Find the category information
  const category = menuCategories.value.find((cat) => cat.id === categoryId);
  const categoryName = category?.name || '';

  // Get existing dish description if editing
  let existingDescription = '';
  if (dishId) {
    for (const cat of menuCategories.value) {
      const foundDish = cat.dishes?.find((dish) => dish.id === dishId);
      if (foundDish) {
        existingDescription = foundDish.description || '';
        break;
      }
    }
  }

  // For editing existing dishes, use the dish ID as generation key
  // For new dishes, use the category-name format
  const generationKey = dishId ? `dish-${dishId}` : `${categoryId}-${name}`;

  // Set loading state for this specific dish
  generatingDishes.value[generationKey] = true;

  // Show toast notification
  toast.add({
    severity: 'info',
    summary: t('common.generating'),
    detail: t('menu.dish.generating.message', { dishName: name }),
    life: 3000,
  });

  try {
    // Generate the dish using the new menu context method
    // Pass the dishId (menu_dish_id) for automatic update when editing existing dishes
    const result = await dishesStore.generateMenuDish(name, categoryName, restaurantId, existingDescription, dishId);

    if (result.dish) {
      if (dishId && result.updatedMenuDish) {
        // The backend automatically updated the menu dish, just refresh the categories
        await fetchMenuCategories(menuId.value);
        // Show success toast
        toast.add({
          severity: 'success',
          summary: t('common.success'),
          detail: t('menu.dish.generation.success', { dishName: name }),
          life: 3000,
        });
      } else if (!dishId) {
        // We're creating a new dish
        const createData = {
          name: name, // Keep the original name unchanged
          description: '',
          price: undefined,
          order: undefined,
          dish_id: result.dish.id,
        };

        const createResult = await addDishToCategory(categoryId, createData);
        if (createResult) {
          await fetchMenuCategories(menuId.value);
          // Show success toast
          toast.add({
            severity: 'success',
            summary: t('common.success'),
            detail: t('menu.dish.generation.success', { dishName: name }),
            life: 3000,
          });
        } else {
          // Show error toast if creation failed
          toast.add({
            severity: 'error',
            summary: t('common.error'),
            detail: t('menu.dish.create.error'),
            life: 3000,
          });
        }
      } else {
        // Editing existing dish but no updatedMenuDish returned - fallback to manual update
        let existingDish: MenuDish | null = null;
        for (const cat of menuCategories.value) {
          const foundDish = cat.dishes?.find((dish) => dish.id === dishId);
          if (foundDish) {
            existingDish = foundDish;
            break;
          }
        }

        if (existingDish) {
          const updateData = {
            name: name,
            dish_id: result.dish.id,
            description: existingDish.description,
            price: existingDish.price,
            order: existingDish.order,
          };

          const updateResult = await updateMenuDish(dishId, updateData);
          if (updateResult) {
            await fetchMenuCategories(menuId.value);
            toast.add({
              severity: 'success',
              summary: t('common.success'),
              detail: t('menu.dish.generation.success', { dishName: name }),
              life: 3000,
            });
          } else {
            toast.add({
              severity: 'error',
              summary: t('common.error'),
              detail: t('menu.dish.update.error'),
              life: 3000,
            });
          }
        }
      }
    } else {
      // Show error toast if generation failed
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('menu.dish.generation.error'),
        life: 3000,
      });
    }
  } catch (error) {
    console.error('Error generating menu dish:', error);
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('menu.dish.generation.error'),
      life: 3000,
    });
  } finally {
    // Remove loading state
    delete generatingDishes.value[generationKey];
  }
};

// Reset state when dish dialog closes
watch(showDishDialog, (newValue) => {
  if (!newValue) {
    selectedDishFromRecipes.value = null;
    selectedDishTitleFromRecipes.value = '';
  }
});

function redirectToDish(dishId: number) {
  router.push({ path: `/menu/dishes/${dishId}` });
}
</script>

<template>
  <main>
    <div v-if="selectedMenu">
      <!-- Menu Header -->
      <div v-if="!isEditingMenu" class="mb-7 flex items-center justify-between gap-2">
        <h1 class="text-2xl">{{ selectedMenu.name }}</h1>
        <Menu
          v-if="can('menus', 'update') || can('menus', 'delete')"
          id="overlay_menu"
          ref="menuOptions"
          :model="menuOptionsItems"
          :popup="true"
          :pt="{ submenuHeader: { class: '!p-0' } }"
          :pt-options="{ mergeProps: true }"
        />
        <Button
          v-if="can('menus', 'update') || can('menus', 'delete')"
          link
          rounded
          :pt="{ root: { class: '!p-1.5 !w-8 !h-8' } }"
          :pt-options="{ mergeProps: true }"
          @click="toggleMenuOptions"
        >
          <template #icon>
            <ButtonMenu />
          </template>
        </Button>
      </div>

      <!-- Edit Menu Name -->
      <div v-else class="items-top mb-7 flex justify-between gap-2">
        <Textarea v-model="editMenuName" class="w-full" rows="2" />
        <Button link rounded :pt="{ root: { class: '!p-1.5 !w-8 !h-8' } }" :pt-options="{ mergeProps: true }" @click="isEditingMenu = false">
          <template #icon>
            <ButtonClose />
          </template>
        </Button>
        <Button link rounded :pt="{ root: { class: '!py-1.5 !px-0 !w-12 !h-8' } }" :pt-options="{ mergeProps: true }" @click="handleUpdateMenu">
          <template #icon>
            <ButtonConfirm />
          </template>
        </Button>
      </div>

      <!-- Menu Categories -->
      <div class="mt-4">
        <h2 class="mb-4 text-xl">{{ t('menu.categories.label') }}</h2>

        <p v-if="isLoading" class="mt-4 italic">{{ t('common.loading') }}</p>

        <div v-else-if="menuCategories.length === 0" class="py-8 text-center text-gray-500">
          <p>{{ t('menu.category.noCategories') }}</p>
          <Button
            v-if="can('menus', 'update')"
            :label="t('menu.category.addFirst')"
            icon="pi pi-plus"
            class="mt-4"
            @click="showCategoryDialog = true"
          />
        </div>

        <Accordion v-else v-model:active-index="categoryActiveIndex" :multiple="true" class="mt-4">
          <AccordionTab v-for="(category, idx) in menuCategories" :key="category.id">
            <template #header>
              <div :ref="(el) => setCategoryHeaderRef(el as HTMLElement | null, idx)" class="flex w-full items-center justify-between">
                <span class="font-semibold">{{
                  te(`menu.categories.${category.name}`) ? t(`menu.categories.${category.name}`) : category.name
                }}</span>
                <div class="flex gap-2">
                  <Button
                    v-if="can('menus', 'update')"
                    icon="pi pi-pencil"
                    size="small"
                    severity="secondary"
                    :title="t('menu.category.update.label')"
                    @click.stop="handleEditCategory(category)"
                  />
                </div>
              </div>
            </template>

            <div
              v-if="(category.dishes && category.dishes.length > 0) || Object.keys(generatingDishes).some((key) => key.startsWith(category.id + '-'))"
              class="space-y-4"
            >
              <!-- Existing dishes -->
              <div
                v-for="dish in category.dishes"
                v-show="!generatingDishes[`dish-${dish.id}`]"
                :key="dish.id"
                class="h-32 border-b border-gray-100 py-2 text-sm text-primary-950"
                :class="{ 'cursor-pointer': can('menus', 'update') }"
                @click="can('menus', 'update') ? handleEditDish(dish) : undefined"
              >
                <div class="flex flex-col items-start">
                  <div class="flex w-full min-w-0 flex-1 flex-row items-center justify-between">
                    <div class="flex min-w-0 flex-1 flex-col gap-3">
                      <div class="flex flex-col">
                        <p class="line-clamp-2 min-w-0 flex-1 text-sm leading-5">{{ dish.name || dish.dish?.name }}</p>
                        <div v-if="dish.dish_id && dish.dish">
                          <p v-if="can('menus', 'update')" class="text-xs italic text-surface-500">
                            {{ t('menu.dish.addFromRecipesInfo') }}
                          </p>
                          <p
                            v-else
                            class="cursor-pointer text-sm italic text-primary-600 hover:text-primary-700"
                            @click="redirectToDish(dish.dish_id)"
                          >
                            <i class="pi pi-arrow-up-right mr-1"></i>
                            {{ t('menu.dish.viewOriginalDish') }}
                          </p>
                        </div>
                      </div>
                      <p v-if="dish.description" class="line-clamp-2 min-w-0 text-xs text-gray-600">{{ dish.description }}</p>
                    </div>
                    <p v-if="dish.price" class="w-16 min-w-0 font-bold text-xs text-primary-600">
                      {{ userStore.user?.active_restaurant?.currency_symbol }} {{ dish.price }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Generating dishes -->
              <!-- For new dishes: category-name format -->
              <div
                v-for="generationKey in Object.keys(generatingDishes).filter((key) => key.startsWith(category.id + '-') && generatingDishes[key])"
                :key="`generating-new-${generationKey}`"
                class="h-32 border-b border-gray-100 py-2 text-sm text-primary-950 opacity-60"
              >
                <div class="flex flex-col items-start">
                  <div class="flex w-full min-w-0 flex-1 flex-row items-center justify-between">
                    <div class="flex min-w-0 flex-1 flex-col gap-3">
                      <div class="flex flex-col">
                        <div class="flex items-center gap-2">
                          <i class="pi pi-spin pi-spinner text-primary-500"></i>
                          <p class="line-clamp-2 min-w-0 flex-1 text-sm leading-5">{{ generationKey.split('-').slice(1).join('-') }}</p>
                        </div>
                        <p class="text-xs italic text-surface-500">{{ t('common.generating') }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- For editing existing dishes: dish-id format -->
              <div
                v-for="dish in (category.dishes || []).filter((dish) => generatingDishes[`dish-${dish.id}`])"
                :key="`generating-edit-${dish.id}`"
                class="h-32 border-b border-gray-100 py-2 text-sm text-primary-950 opacity-60"
              >
                <div class="flex flex-col items-start">
                  <div class="flex w-full min-w-0 flex-1 flex-row items-center justify-between">
                    <div class="flex min-w-0 flex-1 flex-col gap-3">
                      <div class="flex flex-col">
                        <div class="flex items-center gap-2">
                          <i class="pi pi-spin pi-spinner text-primary-500"></i>
                          <p class="line-clamp-2 min-w-0 flex-1 text-sm leading-5">{{ dish.name || dish.dish?.name }}</p>
                        </div>
                        <p class="text-xs italic text-surface-500">{{ t('common.generating') }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-else-if="
                !Object.keys(generatingDishes).some(
                  (key) =>
                    key.startsWith(category.id + '-') ||
                    (key.startsWith('dish-') && (category.dishes || []).some((dish) => key === `dish-${dish.id}`)),
                )
              "
              class="py-4 text-center text-gray-500"
            >
              <p>{{ t('menu.dish.noDishes') }}</p>
            </div>

            <!-- Add dish button inside category -->
            <div v-if="can('menus', 'update')" class="mb-4">
              <div class="flex items-center gap-2">
                <p class="inline-button" @click="(event) => toggleCategoryDishMenu(event, category.id)">+ {{ t('menu.dish.add.label') }}</p>
                <Menu
                  :ref="
                    (el: any) => {
                      if (el) categoryDishMenus[category.id] = el;
                    }
                  "
                  :model="getCategoryDishMenuItems(category.id)"
                  popup
                  :pt="{ submenuHeader: { class: '!p-0' } }"
                  :pt-options="{ mergeProps: true }"
                >
                  <template #item="{ item }">
                    <div class="flex cursor-pointer items-center p-2" @click="(event) => item.command?.({ originalEvent: event, item })">
                      <div class="flex h-5 w-5 items-center justify-center">
                        <i :class="item.icon" class="text-primary-950"></i>
                      </div>
                      <span class="ml-2 font-semibold text-sm">{{ item.label }}</span>
                    </div>
                  </template>
                </Menu>
              </div>
            </div>
          </AccordionTab>
        </Accordion>
      </div>
    </div>

    <div v-else-if="isLoading" class="py-8 text-center">
      <p class="italic">{{ t('common.loading') }}</p>
    </div>

    <div v-else class="py-8 text-center text-gray-500">
      <p>{{ t('menu.notFound') }}</p>
    </div>

    <!-- Category Dialog -->
    <CategoryDialog
      v-model:visible="showCategoryDialog"
      :category="selectedCategory"
      :total-categories="menuCategories.length"
      @save="handleCategorySave"
      @delete="handleCategoryDelete"
    />

    <!-- Dish Dialog -->
    <DishDialog
      v-model:visible="showDishDialog"
      :dish="selectedDish"
      :categories="menuCategories"
      :selected-category-id="selectedCategoryForDish?.id"
      :selected-dish-from-recipes="selectedDishFromRecipes"
      :selected-dish-title-from-recipes="selectedDishTitleFromRecipes"
      @save="handleDishSave"
      @delete="handleDishDelete"
      @generate-from-title="handleGenerateFromTitle"
    />

    <!-- Add Dish From Recipes Dialog -->
    <AddDishFromRecipesDialog
      v-model:visible="showAddFromRecipesDialog"
      @dish-selected="handleDishFromRecipesSelected"
      @dish-title-selected="handleDishTitleFromRecipesSelected"
    />
  </main>
</template>
